import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Button,
  Paper,
  alpha,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import { useGetSalesOrderQuery, usePrintInvoiceNetworkMutation } from '../../../store/api/salesApi';

import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { shareSalesOrderPdf, exportSalesOrderToPdf } from '../utils/exportSalesOrderPdf';
import qzService from '../../../shared/utils/qzService';
import { SalesInvoiceTemplateRenderer } from '../components/SalesInvoiceTemplateRenderer';

function SalesOrderPrint() {
  const { id } = useParams();
  const { data: order, isLoading: loadingOrder, error } = useGetSalesOrderQuery(id);
  const { data: company, isLoading: loadingCompany } = useGetCurrentCompanyQuery();
  const isLoading = loadingOrder || loadingCompany;
  const [printNetwork, { isLoading: isPrinting }] = usePrintInvoiceNetworkMutation();


  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const paperType = company?.printPaperType || (isAndroid ? 'letter' : 'a4');
  const salesTemplate = company?.salesPrintTemplate || 'standard';

  const currency = company?.currency || 'USD';
  const itemFormat = company?.printItemNameFormat || 'name_sku';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';
  const templateLabelMap = {
    standard: 'Template 1 – Standard A4',
    thermal: 'Template 2 – Thermal',
    template3: 'Template 3',
    template4: 'Template 4',
    template5: 'Template 5',
    template6: 'Template 6',
    template7: 'Template 7',
    template8: 'Template 8',
    template9: 'Template 9',
    template10: 'Template 10',
    template11: 'Template 11 – Tax Invoice Grid',
    template12: 'Template 12 – Modern Tax Invoice',
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2) }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatItemName = (item) => {
    let name = formatItemDisplayName(
      { name: item.product?.name, sku: item.product?.sku, brand: item.product?.brand },
      itemFormat
    );
    if (item.isReturn) {
      name += ` (Return${item.isDamaged ? ' - Damaged' : ''})`;
    }
    return name;
  };

  const formatDate = (dateValue, timeValue) => {
    if (!dateValue) return '';
    const dDate = new Date(dateValue);
    if (Number.isNaN(dDate.getTime())) return '';
    const dTime = timeValue ? new Date(timeValue) : dDate;
    const dd = String(dDate.getDate()).padStart(2, '0');
    const mm = String(dDate.getMonth() + 1).padStart(2, '0');
    const yyyy = dDate.getFullYear();
    let hours = dTime.getHours();
    const minutes = String(dTime.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timeStr = `${hours}:${minutes} ${ampm}`;
    let dateStr = '';
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        dateStr = `${mm}/${dd}/${yyyy}`;
        break;
      case 'yyyy-MM-dd':
        dateStr = `${yyyy}-${mm}-${dd}`;
        break;
      case 'dd-MM-yyyy':
        dateStr = `${dd}-${mm}-${yyyy}`;
        break;
      case 'dd/MM/yyyy':
      default:
        dateStr = `${dd}/${mm}/${yyyy}`;
    }
    return `${dateStr} ${timeStr}`;
  };

  const [showTax, setShowTax] = useState(true);
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false);
  const [isQzPrinting, setIsQzPrinting] = useState(false);

  const handleQzPrint = async () => {
    if (!company?.qzPrinterName) {
      alert('No QZ Printer Name configured. Please select a printer in Settings.');
      return;
    }
    setIsQzPrinting(true);
    try {
      const pdfArrayBuffer = await exportSalesOrderToPdf(order, company, {
        itemFormat: company?.printItemNameFormat || 'name_sku',
        filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
        save: false,
      });

      const base64Pdf = btoa(
        new Uint8Array(pdfArrayBuffer)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      await qzService.printPdf(company.qzPrinterName, base64Pdf, {
        jobName: `Invoice-${order.invoiceNumber || order.orderNumber}`,
      });
    } catch (err) {
      alert(`QZ Print failed: ${err.message}`);
    } finally {
      setIsQzPrinting(false);
    }
  };

  useEffect(() => {
    if (!isLoading && order && company?.enableAutoPrintOnSave && !hasAutoPrinted) {
      setHasAutoPrinted(true);
      if (company?.enableQzPrint && company?.qzPrinterName) {
        handleQzPrint();
      } else {
        setShowTax(true);
        setTimeout(() => {
          window.focus();
          window.print();
        }, 500);
      }
    }
  }, [order?.id, company?.id, isLoading, hasAutoPrinted]);

  const handleShare = () => {
    if (!order) return;
    shareSalesOrderPdf(order, company, {
      itemFormat: company?.printItemNameFormat || 'name_sku',
      filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
    });
  };

  const handleDirectPrint = async (silent = false) => {
    try {
      await printNetwork(id).unwrap();
      if (!silent) alert('Sent to network printer successfully');
    } catch (err) {
      const msg = err.data?.message || err.message;
      if (!silent) alert(`Print failed: ${msg}`);
      else console.error('Silent print failed:', msg);
    }
  };

  useEffect(() => {
    if (order && company?.enableNetworkThermalPrinter && company?.enableDirectPrintSilently && !isLoading) {
      handleDirectPrint(true);
    }
  }, [order?.id, company?.id, isLoading]);

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error || !order) return <Typography color="error">Error loading sales order</Typography>;


  const invoiceDate = formatDate(order.invoiceDate, order.createdAt);
  const items = order.items || [];

  const templateKey = [
    'standard',
    'thermal',
    'template3',
    'template4',
    'template5',
    'template6',
    'template7',
    'template8',
    'template9',
    'template10',
    'template11',
    'template12',
    'a5',
  ].includes(salesTemplate)
    ? salesTemplate
    : 'standard';

  const isA5 = templateKey === 'a5' || paperType === 'a5';
  const pageWidth = templateKey === 'thermal' ? '80mm' : (isA5 ? '148mm' : (paperType === 'letter' ? '215.9mm' : '210mm'));

  const renderStandard = () => (
    <Box
      sx={{
        p: 4,
        maxWidth: 900,
        m: '0 auto',
        fontSize: 12,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box
              component="img"
              src={company.logoUrl}
              alt=""
              sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain', display: 'block' }}
            />
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2">Invoice No: {order.invoiceNumber || order.orderNumber}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order.salesType}</Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1, mr: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Bill To</Typography>
          <Typography variant="body2">{order.customer?.name}</Typography>
          {order.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
        </Box>
      </Box>
      <Table size="small" sx={{ mt: 2 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
            {showTax && <TableCell sx={{ fontWeight: 600 }}>VAT</TableCell>}
            <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>{formatItemName(it)}</TableCell>
              <TableCell>{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell>{it.unit || 'pcs'}</TableCell>
              <TableCell>{formatAmount(it.unitPrice)}</TableCell>
              <TableCell>{formatAmount(it.discount || 0)}</TableCell>
              {showTax && <TableCell>{formatAmount(it.vatAmount || 0)}</TableCell>}
              <TableCell sx={{ textAlign: 'right' }}>{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Subtotal</Typography><Typography variant="body2">{formatAmount(order.subtotal)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Discount</Typography><Typography variant="body2">{formatAmount(order.discountTotal)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">VAT</Typography><Typography variant="body2">{formatAmount(order.vatTotal)}</Typography></Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Grand Total</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(order.grandTotal || order.totalAmount)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><Typography variant="caption">Paid</Typography><Typography variant="caption">{formatAmount(order.paidAmount)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Balance</Typography><Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography></Box>
        </Box>
      </Box>
      {order.notes && (
        <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{order.notes}</Typography>
        </Paper>
      )}
    </Box>
  );

  const renderThermal = () => (
    <Box sx={{ p: 2, maxWidth: 420, m: '0 auto', fontSize: 11 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>{company?.name}</Typography>
      <Divider sx={{ my: 1 }} />
      {items.map((it, idx) => (
        <Box key={it.id || idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption">{formatItemName(it)} x{it.quantity}</Typography>
          <Typography variant="caption">{formatAmount(it.total)}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Total</Typography><Typography variant="caption">{formatAmount(order.grandTotal)}</Typography></Box>
    </Box>
  );

  const renderTemplate3 = () => renderStandard();
  const renderTemplate4 = () => renderStandard();
  const renderTemplate5 = () => renderStandard();
  const renderTemplate6 = () => renderStandard();
  const renderTemplate7 = () => renderStandard();
  const renderTemplate8 = () => renderStandard();
  const renderTemplate9 = () => renderStandard();
  const renderTemplate10 = () => renderStandard();

  let printContent;
  if (templateKey === 'thermal') {
    printContent = renderThermal();
  } else if (templateKey === 'template11' || templateKey === 'template12' || templateKey === 'template3' || templateKey === 'a5') {
    printContent = (
      <SalesInvoiceTemplateRenderer
        order={order}
        company={company}
        templateKey={templateKey}
        currency={currency}
        itemFormat={itemFormat}
        dateFormat={dateFormat}
        showTax={showTax}
      />
    );
  } else if (templateKey === 'template3') {
    printContent = renderTemplate3();
  } else if (templateKey === 'template4') {
    printContent = renderTemplate4();
  } else if (templateKey === 'template5') {
    printContent = renderTemplate5();
  } else if (templateKey === 'template6') {
    printContent = renderTemplate6();
  } else if (templateKey === 'template7') {
    printContent = renderTemplate7();
  } else if (templateKey === 'template8') {
    printContent = renderTemplate8();
  } else if (templateKey === 'template9') {
    printContent = renderTemplate9();
  } else if (templateKey === 'template10') {
    printContent = renderTemplate10();
  } else {
    printContent = renderStandard();
  }

  return (
    <Box>
      <style>{`
        @page { 
          size: ${templateKey === 'thermal' ? '80mm auto' : (isA5 ? 'A5' : (paperType === 'letter' ? 'letter' : 'a4'))}; 
          margin: 0;
        }
        @media print {
          body { margin: 0; padding: 0; }
          .print-content {
            width: ${pageWidth} !important;
            max-width: ${pageWidth} !important;
            margin: 0 auto;
            padding: ${isA5 ? '0' : '10mm'};
            border: ${isA5 ? 'none' : '1pt solid #666'} !important;
            box-sizing: border-box !important;
            background: #fff !important;
            min-height: ${isA5 ? 'auto' : '290mm'}; 
          }
          .MuiTypography-root { font-family: 'Helvetica', sans-serif !important; }
          .MuiTableCell-root { padding: 2mm !important; font-size: 9pt !important; }
        }
      `}</style>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, '@media print': { display: 'none' } }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => { setShowTax(true); setTimeout(() => window.print(), 100); }}>Print (with Tax)</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => { setShowTax(false); setTimeout(() => window.print(), 100); }}>Print (No Tax)</Button>
        <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handleShare}>Share</Button>
        {company?.enableQzPrint && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PrintIcon />}
            onClick={() => handleQzPrint()}
            disabled={isQzPrinting}
          >
            {isQzPrinting ? 'Printing...' : `Direct Print (QZ: ${company.qzPrinterName || 'Default'})`}
          </Button>
        )}
        {company?.enableNetworkThermalPrinter && (
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<PrintIcon />} 
            onClick={handleDirectPrint}
            disabled={isPrinting}
          >
            {isPrinting ? 'Printing...' : 'Direct Print (Network)'}
          </Button>
        )}
      </Box>

      <Box className="print-content">
        {printContent}
      </Box>
    </Box>
  );
}

export default SalesOrderPrint;
